import { parseMoney } from '../../api/parseMoney'
import type { components } from '../../api/generated/schema'
import type { RelinkStatus } from './useAggregator'

export type { RelinkStatus }

/**
 * `GET /aggregator/items/{id}/relink` (`RelinkState`) read into app types.
 * The ONLY place `RelinkAccountSummary.current_balance` — a decimal string —
 * becomes cents, through `parseMoney`, once. A liability is positive on the
 * wire; the screen displays it through `signedCents(cents, groupOf(type))`.
 */
export type RelinkAccount = {
  id: string
  name: string
  mask: string | null
  type: string
  subtype: string | null
  currency: string
  balanceCents: number | null
  /** `YYYY-MM-DD`, kept as the wire's string so no timezone shift can happen. */
  lastTransactionDate: string | null
}

export type Relink = {
  status: RelinkStatus
  fromItemId: string
  paired: { oldId: string; newId: string; reason: string }[]
  unmatchedOld: RelinkAccount[]
  unmatchedNew: RelinkAccount[]
  suggestions: { oldId: string; newId: string; overlap: number }[]
  keptSeparate: string[]
}

function toRelinkAccount(a: components['schemas']['RelinkAccountSummary']): RelinkAccount {
  return {
    id: a.id,
    name: a.name,
    mask: a.mask ?? null,
    type: a.type,
    subtype: a.subtype ?? null,
    currency: a.currency,
    balanceCents: a.current_balance != null ? parseMoney(a.current_balance) : null,
    lastTransactionDate: a.last_transaction_date ?? null,
  }
}

export function toRelink(r: components['schemas']['RelinkState']): Relink {
  return {
    status: r.status,
    fromItemId: r.from_item_id,
    paired: r.paired.map((p) => ({ oldId: p.old_account_id, newId: p.new_account_id, reason: p.reason })),
    unmatchedOld: r.unmatched_old.map(toRelinkAccount),
    unmatchedNew: r.unmatched_new.map(toRelinkAccount),
    suggestions: r.suggestions.map((s) => ({
      oldId: s.old_account_id,
      newId: s.new_account_id,
      overlap: s.overlap,
    })),
    keptSeparate: r.kept_separate ?? [],
  }
}

/**
 * Candidates for one old account: the new accounts in the SAME currency
 * (the server answers `422 currency_mismatch` otherwise), those the server
 * suggested first by overlap descending, then the rest in wire order. A
 * suggestion is only a hint — nothing here pre-selects it.
 */
export function candidatesFor(
  relink: Relink,
  oldId: string,
): { account: RelinkAccount; overlap: number | null }[] {
  const old = relink.unmatchedOld.find((a) => a.id === oldId)
  if (old === undefined) return []
  const overlapOf = new Map(
    relink.suggestions.filter((s) => s.oldId === oldId).map((s) => [s.newId, s.overlap] as const),
  )
  const same = relink.unmatchedNew
    .filter((n) => n.currency === old.currency)
    .map((account) => ({ account, overlap: overlapOf.get(account.id) ?? null }))
  const suggested = same
    .filter((c) => c.overlap !== null)
    .sort((a, b) => (b.overlap ?? 0) - (a.overlap ?? 0))
  const rest = same.filter((c) => c.overlap === null)
  return [...suggested, ...rest]
}
